import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@xing-com/button';
import { Card as BaseCard } from '@xing-com/card';
import { AI_SEARCH_FEEDBACK_SURVERY_URL } from '@xing-com/crate-jobs-constants';
import { InViewTracking } from '@xing-com/crate-jobs-tracking';
import { useExperiment, useViewerData } from '@xing-com/hub';
import { mediaConfined } from '@xing-com/layout-tokens';
import { spaceL, spaceXL, spaceXXL } from '@xing-com/tokens';
import { BodyCopy } from '@xing-com/typography';

import type { FeedbackVariant } from './color-feedback';
import { FeedbackContent } from './feedback-content';
import { MatchingReasonsFeedback } from './matching-reasons-feeback';

type Props = {
  searchId: string;
  trackingToken?: string;
};

export const AIFeedback: React.FC<Props> = ({ searchId, trackingToken }) => {
  const [showSurvey, setShowSurvey] = useState(false);
  const { data } = useViewerData();
  const hashedId = data?.hashedUserId ?? '';
  const isMREnabled = useExperiment('ABACUS-484') === 'B';

  const nwtTrackingOptions = {
    eventSchema: 'aicomponent',
    page: 'jobs_search/serp',
    event: 'entered_viewport',
    componentName: 'ai_search',
    componentFeedbackLevel: '2',
    trackingToken,
  } as const;

  if (isMREnabled) {
    return (
      <MatchingReasonsFeedback
        nwtTrackingOptions={nwtTrackingOptions}
        hashedId={hashedId}
        trackingToken={trackingToken}
      />
    );
  }

  const handleOnClick = (feedback: FeedbackVariant): void => {
    if (feedback === 'dislike') {
      setShowSurvey(true);
    }
  };

  const urlParams = new URLSearchParams({
    h: hashedId,
    search_id: searchId,
  });
  const surveyUrl = hashedId
    ? `${AI_SEARCH_FEEDBACK_SURVERY_URL}?${urlParams.toString()}`
    : AI_SEARCH_FEEDBACK_SURVERY_URL;

  return (
    <InViewTracking nwtTrackingOptions={nwtTrackingOptions}>
      <Card>
        <CardContent>
          {!showSurvey ? (
            <FeedbackContent
              onClick={handleOnClick}
              trackingToken={trackingToken}
            />
          ) : (
            <SadFeedbackContainer>
              <BodyCopy noMargin as="h2" tabIndex={0}>
                <FormattedMessage id="JOBS_SEARCH_AI_FEEDBACK_BAD_COPY" />
              </BodyCopy>
              <Button
                variant="secondary"
                href={surveyUrl}
                // @ts-expect-error FIXME: SC6
                target="_blank"
                rel="noopener"
              >
                <FormattedMessage id="JOBS_SEARCH_AI_FEEDBACK_BAD_BUTTON" />
              </Button>
            </SadFeedbackContainer>
          )}
        </CardContent>
      </Card>
    </InViewTracking>
  );
};

const Card = styled(BaseCard)`
  margin: ${spaceXL} 0;

  @media ${mediaConfined} {
    margin: ${spaceXXL} 0;
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spaceL};

  @media ${mediaConfined} {
    flex-direction: row;
    justify-content: space-between;
  }
`;

const SadFeedbackContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: ${spaceL};

  @media ${mediaConfined} {
    flex-direction: row;
    gap: 0;
  }
`;
